<template>
  <BgImageLayout>
    <ConfirmationDialog
      ref="confirmDialog"
      title="Confirm"
      confirm-label="Yes, delete"
      confirm-btn-type="danger"
      cancel-label="no, keep resource"
    />

    <CreateDepartmentModal ref="modal" @created="departmentCreated" />

    <RModal ref="editEntityModal" title="Edit entity name" @cancel="$event()" @ok="updateEntityName">
      <RInput
        v-model.trim="newEntityName"
        class="q-pt-lg"
        autofocus
        label="Entity name"
        no-hint
        no-feedback-icon
        on-clear="focus"
      />
    </RModal>

    <RCard class="departments relative-position">
      <AgGrid
        v-if="dynamicCols"
        ref="agGrid"
        :options="gridOptions"
        :row-data="departments"
        @ready="agGrid = $event"
        @cell-value-changed="cellValueChanged"
      >
        <template #header>
          <Grid gaf="column" justify-content="space-between" align-items="center">
            <Grid gaf="column" justify-content="start" align-items="center" class="text-subtitle-1 text-secondary-2">
              {{ departmentLabel.plural }}

              <HelpPageBtn :page="__guide.departments">
                <LmTooltip>
                  {{ departmentLabel.plural }}
                </LmTooltip>
              </HelpPageBtn>

              <q-btn dense size="0.675rem" flat round icon="edit" color="primary" @click="onEditEntityName">
                <LmTooltip>Edit entity name</LmTooltip>
              </q-btn>
            </Grid>

            <Grid gaf="column" align-items="center" gap="var(--r-space-md)">
              <ExportBtn @click="exportToExcel" />
              <RBtn type="primary" size="small" @click="onCreateDepartment">
                Create {{ departmentLabel.singular.toLowerCase() }}
              </RBtn>
              <RBtn type="link" to="/financial">go to financial insights</RBtn>
              <ShowHideCols :ag-grid="agGrid" />
            </Grid>
          </Grid>
        </template>
      </AgGrid>

      <QInnerLoading :showing="!dynamicCols">
        <QSpinnerHourglass size="3.5rem" color="primary-1" />
      </QInnerLoading>
    </RCard>
  </BgImageLayout>
</template>

<script>
import * as apiTo from '@/modular-api'
import { agPriceCol } from '@/ag-grid/ag-columns'
import { formatPrice } from '@/lib/formatting.utils'
import { gridOptions } from './gridOptions'
// Libraries
import { mapGetters } from 'vuex'
// Components
import AgGrid from '@/ag-grid/AgGrid'
import ShowHideCols from '@/ag-grid/components/show-hide-cols/ShowHideCols'
import HelpPageBtn from '@/reusable/base-components/HelpPageBtn'
import ConfirmationDialog from '@/ui/ConfirmationDialog'
import RModal from '@/ui/RModal'
import CreateDepartmentModal from './components/CreateDepartmentModal'
import ExportBtn from '@/reusable/ExportBtn'

export default {
  components: {
    AgGrid,
    ShowHideCols,
    HelpPageBtn,
    ConfirmationDialog,
    RModal,
    CreateDepartmentModal,
    ExportBtn
  },

  mixins: [gridOptions],

  data: () => ({
    departments: null,
    dynamicCols: null,
    agGrid: null,
    newEntityName: null
  }),

  computed: {
    ...mapGetters('entity-labels', ['departmentLabel']),

    bottomPinnedRow() {
      if (!this.departments || !this.departments.length) return null
      return this.departments.reduce((acc, curr) => {
        this.generateBottomRow(acc, curr)
        return acc
      }, {})
    }
  },

  created() {
    this.getDepsAndDynamicColumns()
  },

  methods: {
    getDepsAndDynamicColumns() {
      Promise.all([apiTo.GET_DEPARTMENTS_WITH_BUDGETS(), apiTo.GET_DEPARTMENTS_BUDGET_YEAR_COLS()]).then(
        ([{ data: departmentsResponse }, { data: columnsResponse }]) => {
          this.departments = departmentsResponse.result
          this.generateDynamicCols(columnsResponse.result)
        }
      )
    },

    generateBottomRow(acc, curr) {
      for (let [key, value] of Object.entries(curr)) {
        if (['name', 'id', 'cost_center'].includes(key)) {
          acc[key] = ''
          if (key === 'name') acc.name = 'Total Budgets'
          continue
        }
        if (!value) continue
        if (acc.hasOwnProperty(key)) {
          const res = Number(acc[key].amount) + Number(value.amount)
          acc[key].amount = res.toFixed(2)
          if (value.currency) acc[key].currency = value.currency
        } else {
          acc[key] = { amount: Number(value.amount) }
        }
      }
    },

    generateDynamicCols(cols) {
      this.dynamicCols = cols.reduce((acc, col) => {
        acc.push(
          agPriceCol(`${col.name} budget`, col.system_name + '.amount', {
            hide: col.hide,
            maxWidth: 300,
            editable: params => params.data.name !== 'Total Budgets',
            cellClassRules: {
              'editable-cell': params => params.data.name !== 'Total Budgets',
              'ag-right-aligned-cell': params => true
            },
            cellRenderer: params => this.getPriceWithCurrency(params, col)
          })
        )
        return acc
      }, [])
    },

    getPriceWithCurrency(params, col, withHtml = true) {
      const currency = params.data[col.system_name].currency || ''
      const price = formatPrice(params.data[col.system_name].amount)
      let text

      if (withHtml) {
        text = price ? `${price} <small>${currency}</small>` : 'N/A'
      } else text = price ? `${price} ${currency}` : 'N/A'

      if (params.data.name === 'Total Budgets') return `<b>${text}</b>`
      return text
    },

    onDeleteDep({ data }) {
      const depId = data.id

      apiTo.GET_DEPARTMENT_DELETE_CONFIRMATION(depId).then(({ data }) => {
        const body = data.messages.default.join('\n')
        const onCancel = hide => hide()

        const dialog = this.$refs.confirmDialog.show({ body })

        dialog.onCancel(onCancel)
        dialog.onConfirm(this.deleteDep.bind(this, depId))
      })
    },

    deleteDep(depId, hideModal, setModalLoading) {
      setModalLoading()

      apiTo
        .DELETE_DEPARTMENT(depId)
        .then(() => {
          hideModal()
          const index = this.departments.findIndex(dep => dep.id === depId)
          if (~index) this.departments.splice(index, 1)
          this.updateBudgetTotalsRow()
        })
        .finally(() => setModalLoading(false))
    },

    departmentCreated(newDepartment) {
      this.departments.push(newDepartment)
      this.updateBudgetTotalsRow()
    },

    didValueChanged(old, newVal) {
      if (typeof old === 'string' && typeof newVal === 'number') return Number(old) !== Number(newVal)
      return old !== newVal
    },

    cellValueChanged(event) {
      if (!this.didValueChanged(event.oldValue, event.newValue)) return

      const { id, ...payload } = event.data

      apiTo.UPDATE_DEPARTMENT(id, payload).then(({ data }) => {
        const depToUpdateIndex = this.departments.findIndex(d => d.id === data.result.id)
        if (~depToUpdateIndex) this.departments.splice(depToUpdateIndex, 1, data.result)

        this.$refs.agGrid.updateRow(id, data.result)
        this.updateBudgetTotalsRow()
      })
    },

    exportToExcel() {
      const allColumns = this.agGrid.columnApi.getAllColumns()
      const visibleColumns = this.agGrid.columnApi.getAllDisplayedColumns()
      const columnsToExport = visibleColumns.reduce((acc, curr) => {
        if (['actions', 'spacer-1', 'spacer-2'].includes(curr.colId)) return acc

        acc.push(curr)

        if (curr.colId.includes('amount')) {
          const text = curr.colId.split('.')[0]
          const currencyCol = allColumns.find(col => col.colId === `${text}-currency`)
          acc.push(currencyCol)
        }
        return acc
      }, [])

      this.agGrid.api.exportDataAsExcel({
        columnKeys: columnsToExport,
        fileName: 'departments',
        sheetName: 'departments'
      })
    },

    updateBudgetTotalsRow() {
      this.$nextTick(() => {
        const row = this.bottomPinnedRow ? [this.bottomPinnedRow] : []
        this.agGrid.api.setPinnedBottomRowData(row)
      })
    },

    onCreateDepartment() {
      this.$refs.modal.show()
    },

    onEditEntityName() {
      this.newEntityName = this.departmentLabel.label
      this.$refs.editEntityModal.show()
    },

    updateEntityName(hideModal, setModalLoading) {
      setModalLoading()

      this.$store
        .dispatch('entity-labels/updateEntityLabel', {
          entity: this.departmentLabel.entity,
          label: this.departmentLabel,
          newName: this.newEntityName
        })
        .then(hideModal)
        .finally(() => setModalLoading(false))
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins';

.departments {
  @include table-with-no-filters;
  min-height: 300px;

  @include custom-scrollbar('::v-deep .ag-body-viewport');

  ::v-deep .ag-body-viewport {
    max-height: calc(100vh - 290px);
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }
  }
}
</style>
